import { z, type ZodTypeAny } from 'zod';
import type IPageable from './IPageable';

/**
 * Creates a Zod schema for an object that implements {@link IPageable}.
 * This schema is intended for validating the DTO (Data Transfer Object) form
 * of a pageable collection.
 *
 * @template TItemSchema - A Zod schema for the individual items in the pages.
 * @param itemSchema - The Zod schema for an individual item.
 * @returns A Zod schema for a pageable DTO.
 */
export const PageableDTOSchema = <TItemSchema extends ZodTypeAny>(
  itemSchema: TItemSchema
): z.ZodObject<
  {
    pages: z.ZodArray<z.ZodArray<TItemSchema, 'many'>, 'many'>;
    allItems: z.ZodArray<TItemSchema, 'many'>;
    pageSize: z.ZodNumber;
    currentPage: z.ZodNumber;
    totalItems: z.ZodNumber;
    totalPages: z.ZodNumber;
    hasNext: z.ZodBoolean;
    hasPrevious: z.ZodBoolean;
    isLoadingNewPage: z.ZodBoolean;
  },
  'strip', // or 'strict' or 'passthrough' depending on desired behavior
  z.ZodTypeAny,
  {
    pages: Array<Array<z.infer<TItemSchema>>>;
    allItems: Array<z.infer<TItemSchema>>;
    pageSize: number;
    currentPage: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
    isLoadingNewPage: boolean;
  },
  {
    pages: Array<Array<z.input<TItemSchema>>>;
    allItems: Array<z.input<TItemSchema>>;
    pageSize: number;
    currentPage: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
    isLoadingNewPage: boolean;
  }
> =>
  z.object({
    pages: z.array(z.array(itemSchema)),
    allItems: z.array(itemSchema),
    pageSize: z.number(),
    currentPage: z.number(), // 0-indexed
    totalItems: z.number(),
    totalPages: z.number(),
    hasNext: z.boolean(),
    hasPrevious: z.boolean(),
    isLoadingNewPage: z.boolean()
    // Methods like loadPage, nextPage, previousPage are not part of a DTO.
  });
